import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useInView, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { SectionHeading } from './fx/SectionHeading';
import { Camera } from 'lucide-react';
import { GALLERY, INSTAGRAM_URL, INSTAGRAM_HANDLE } from '@/data/site';
import { useScrollLock } from '@/lib/experience';
import { Photo } from './fx/Photo';
import { LanaTribute, ensureTributeFonts } from './LanaTribute';
import { Wave } from './fx/Wave';
import { burst } from './fx/Burst';
import { InstagramIcon, ArrowUpRight, Heart, DoodleArrow } from './brand/Shapes';

const BG = '#FFF5EE'; // pêssego clarinho (fundo dos depoimentos no site original)
const OFFSETS = ['-translate-x-[4%]', 'translate-x-[6%]', 'translate-x-[2%]', '-translate-x-[3%]'];
const ROTATE = [-3, 2.5, -1.5, 3];
const TAPES = ['bg-pet-pink/85', 'bg-pet-mint/95', 'bg-pet-sky/95', 'bg-pet-peach/95', 'bg-pet-lilac/85'];

/** Duas fotos em sequência viram uma mini-animação (Yorkshire fofo ↔ lambendo o focinho). */
const useFlip = (enabled, ms = 950) => {
  const [flip, setFlip] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setFlip((f) => !f), ms);
    return () => clearInterval(id);
  }, [enabled, ms]);
  return flip;
};

const FlipImage = ({ item, active, sizes, className, eager = false }) => {
  const reduce = useReducedMotion();
  const flip = useFlip(Boolean(item.lg2) && active && !reduce);
  return (
    <>
      <Photo photo={item} sizes={sizes} className={className} eager={eager} />
      {item.lg2 && (
        <img
          src={item.lg2}
          srcSet={item.srcSet2}
          sizes={sizes}
          alt={item.alt2}
          loading={eager ? 'eager' : 'lazy'}
          className={`${className} absolute inset-0 transition-opacity duration-150 ${flip ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </>
  );
};

/** Polaroid com fita adesiva, legenda escrita à mão e coração de curtir. */
const Card = ({ item, index, parallax, onOpen, eager, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: '0px 200px' });
  const [liked, setLiked] = useState(false);
  return (
    <div className={`shrink-0 ${OFFSETS[index % OFFSETS.length]} ${className}`}>
      <motion.figure
        ref={ref}
        className={`relative w-min p-2.5 pb-3 md:p-3 md:pb-4 rounded-[16px] ${
          item.memorial
            ? 'bg-[#FFF8EE] shadow-[0_0_0_3px_#F6D08A,0_0_40px_6px_rgba(246,208,138,0.55)]'
            : 'bg-white shadow-[0_24px_50px_-24px_rgba(45,55,72,0.5)]'
        }`}
        style={{ rotate: item.memorial ? -1 : ROTATE[index % ROTATE.length] }}
        whileHover={{ rotate: 0, scale: 1.04, y: -10 }}
        transition={{ type: 'spring', stiffness: 300, damping: 14 }}
      >
        {item.memorial && (
          <span className="absolute -top-3.5 -right-3 z-20 rounded-full bg-[#FFF8EE] px-2.5 py-1 text-[11px] font-extrabold text-[#8A5A3C] shadow-md ring-2 ring-[#F6D08A]">
            em memória ✨
          </span>
        )}
        <span
          className={`absolute -top-3 left-1/2 -translate-x-1/2 w-20 h-6 ${
            item.memorial ? 'bg-[linear-gradient(90deg,#F7B4B4,#F8CFA3,#F7E6A1,#C9E6B3,#B6DAF2,#C9C0EE)] opacity-90' : TAPES[index % TAPES.length]
          } rounded-sm shadow-sm z-10`}
          style={{ transform: `translateX(-50%) rotate(${index % 2 ? 6 : -5}deg)` }}
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => onOpen(index)}
          data-cursor={item.memorial ? 'Lana 💛' : 'Ver 💗'}
          aria-label={item.memorial ? 'Abrir homenagem em memória de Lana' : `Ampliar foto: ${item.caption}`}
          className="relative block h-[27svh] md:h-[31vh] rounded-[10px] overflow-hidden bg-pet-peach/40"
          style={{ aspectRatio: item.ratio }}
        >
          <motion.div className="absolute inset-[-8%]" style={{ x: parallax }}>
            <FlipImage item={item} active={inView} eager={eager} sizes="(min-width: 768px) 24vw, 46vw" className="w-full h-full object-cover" />
          </motion.div>
        </button>
        <figcaption className="mt-2.5 flex items-center justify-between gap-3 px-1">
          <span className="min-w-0 flex-1 font-hand text-lg md:text-xl leading-tight text-pet-ink line-clamp-2">{item.caption}</span>
          <motion.button
            type="button"
            aria-label={liked ? 'Descurtir foto' : 'Curtir foto'}
            aria-pressed={liked}
            onClick={(e) => {
              if (!liked) burst(e, 'heart', 10);
              setLiked((l) => !l);
            }}
            whileTap={{ scale: 0.7 }}
            animate={liked ? { scale: [1, 1.35, 1] } : { scale: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 12 }}
            className={`like-btn shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${liked ? 'bg-pet-pink-deep text-white shadow-md' : 'bg-pet-pink-soft text-pet-pink-deep/70 hover:text-pet-pink-deep'}`}
          >
            <Heart className="w-5 h-5" />
          </motion.button>
        </figcaption>
      </motion.figure>
    </div>
  );
};

const Lightbox = ({ index, onClose, onStep }) => {
  useScrollLock(index !== null);
  const item = index !== null ? GALLERY[index] : null;

  useEffect(() => {
    if (index === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onStep(1);
      if (e.key === 'ArrowLeft') onStep(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, onClose, onStep]);

  return createPortal(
    <AnimatePresence>
      {item && (
        <motion.div
          className="fixed inset-0 z-[125] bg-pet-blue/95 backdrop-blur-md flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          role="dialog"
          aria-modal="true"
          aria-label="Foto ampliada"
          data-lenis-prevent
        >
          <div className="flex items-center justify-between px-5 md:px-8 pt-5 text-white">
            <span className="font-display text-lg tabular-nums">
              {index + 1} / {GALLERY.length}
            </span>
            <button type="button" onClick={onClose} aria-label="Fechar" className="w-12 h-12 rounded-full bg-white text-pet-blue text-2xl leading-none font-bold shadow-lg">
              ×
            </button>
          </div>
          <div className="relative flex-1 flex items-center justify-center px-4 md:px-24 py-4 min-h-0" onClick={onClose}>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={index}
                className="relative max-h-full h-full flex items-center"
                initial={{ opacity: 0, scale: 0.8, rotate: -4 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.8, rotate: 4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.6}
                onDragEnd={(_, info) => {
                  if (info.offset.x < -70) onStep(1);
                  else if (info.offset.x > 70) onStep(-1);
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative max-h-full h-full bg-white p-2 md:p-3 rounded-[18px] shadow-2xl" style={{ aspectRatio: item.ratio }}>
                  <div className="relative w-full h-full rounded-[12px] overflow-hidden">
                    <FlipImage item={item} active eager sizes="90vw" className="w-full h-full object-cover" />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            <button type="button" onClick={(e) => { e.stopPropagation(); onStep(-1); }} aria-label="Foto anterior" className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white text-pet-blue items-center justify-center text-2xl font-bold shadow-lg">
              ←
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); onStep(1); }} aria-label="Próxima foto" className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white text-pet-blue items-center justify-center text-2xl font-bold shadow-lg">
              →
            </button>
          </div>
          <div className="px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] md:pb-8 text-center text-white">
            <p className="font-hand text-2xl md:text-3xl">{item.caption}</p>
            <p className="md:hidden mt-1 text-xs font-bold text-white/70">Deslize para os lados</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const InstagramCard = () => (
  <motion.a
    href={INSTAGRAM_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-cursor="Seguir!"
    whileHover={{ rotate: -2, scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    transition={{ type: 'spring', stiffness: 300, damping: 12 }}
    className="group snap-center shrink-0 w-[66vw] sm:w-[46vw] lg:w-[30vw] h-[48svh] md:h-[56vh] rounded-[40px] bg-gradient-to-br from-[#F77737] via-[#E1306C] to-[#9B7BD4] text-white p-7 md:p-10 flex flex-col justify-between shadow-[0_30px_60px_-30px_rgba(225,48,108,0.7)]"
  >
    <span className="w-16 h-16 rounded-3xl bg-white/20 flex items-center justify-center">
      <InstagramIcon className="w-9 h-9" />
    </span>
    <div>
      <p className="font-display font-medium text-[clamp(2rem,4.2vw,3.4rem)] leading-[1.05]">Tem muito mais fofura no Instagram!</p>
      <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-[#E1306C] px-4 py-2 font-extrabold">
        {INSTAGRAM_HANDLE}
        <ArrowUpRight className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </p>
    </div>
  </motion.a>
);

const Heading = ({ align }) => (
  <SectionHeading
    align={align}
    kicker="Galeria"
    icon={Camera}
    kickerClass="bg-pet-pink-soft text-pet-pink-ink"
    parts={[{ text: 'Momentos' }, { text: 'especiais', className: 'text-pet-pink-deep sticker-text' }]}
    note="Confira alguns dos nossos amiguinhos que já passaram por aqui. Cada pet é único e merece todo o carinho!"
  >
    <p className="mt-6 font-hand text-xl md:text-2xl text-pet-blue">
      <span>
        role e deixe 
        <span className="whitespace-nowrap">
          um coração
          <DoodleArrow className="inline-block align-middle ml-1 w-10 h-7 text-pet-pink-deep rotate-[-10deg]" />
        </span>
      </span>
    </p>
  </SectionHeading>
);

export const Gallery = () => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [dist, setDist] = useState(0);
  const [open, setOpen] = useState(null);
  // o trilho é horizontal: o lazy-load nativo não percebe as fotos à direita,
  // então carregamos todas quando a seção se aproxima
  const near = useInView(sectionRef, { margin: '800px 0px', once: true });
  useEffect(() => {
    if (near) ensureTributeFonts();
  }, [near]);

  useLayoutEffect(() => {
    if (!trackRef.current) return undefined;
    const measure = () => {
      if (trackRef.current) setDist(Math.max(0, trackRef.current.scrollWidth - window.innerWidth));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(trackRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] });
  // segura a abertura parada no começo antes de deslizar
  const x = useTransform(scrollYProgress, [0.08, 1], [0, -dist]);
  const parallax = useTransform(scrollYProgress, [0, 1], ['5%', '-5%']);
  const barOpacity = useTransform(scrollYProgress, [0, 0.93, 1], [1, 1, 0]);

  const [tribute, setTribute] = useState(false);
  const closeTribute = useCallback(() => setTribute(false), []);
  // a foto da Lana abre a homenagem; o visualizador normal pula a foto dela
  const openItem = useCallback((i) => (GALLERY[i].memorial ? setTribute(true) : setOpen(i)), []);
  const step = useCallback(
    (dir) =>
      setOpen((i) => {
        let next = i;
        do {
          next = (next + dir + GALLERY.length) % GALLERY.length;
        } while (GALLERY[next].memorial);
        return next;
      }),
    []
  );
  const close = useCallback(() => setOpen(null), []);

  return (
    <section
      id="galeria"
      data-nav="galeria"
      ref={sectionRef}
      className="relative"
      style={{ height: `calc(100svh + ${dist}px)`, background: BG }}
    >
      <Wave color={BG} className="absolute bottom-[calc(100%-1px)] inset-x-0" />
      <div className="sticky top-0 h-[100svh] overflow-hidden flex flex-col justify-center">
        <motion.div ref={trackRef} className="flex items-center gap-5 md:gap-14 pl-5 sm:pl-8 lg:pl-12 pr-[8vw] w-max will-change-transform" style={{ x }}>
          <div className="shrink-0 w-[84vw] sm:w-[60vw] lg:w-[38vw] pr-2 md:pr-8">
            <Heading align="left" />
          </div>

          {/* mural em duas fileiras */}
          <div className="grid grid-flow-col grid-rows-2 gap-x-4 md:gap-x-9 gap-y-5 md:gap-y-7 items-center justify-items-center">
            {GALLERY.map((item, i) => (
              <Card key={item.name} item={item} index={i} parallax={parallax} onOpen={openItem} eager={near} />
            ))}
          </div>

          <InstagramCard />
        </motion.div>

        <motion.div className="container-cp absolute bottom-5 md:bottom-8 inset-x-0 flex items-center gap-4" style={{ opacity: barOpacity }} aria-hidden="true">
          <span className="font-display text-sm text-pet-ink/60">{GALLERY.length} fotos</span>
          <div className="relative flex-1 h-2 bg-white rounded-full overflow-hidden">
            <motion.div className="absolute inset-0 origin-left bg-gradient-to-r from-pet-pink to-pet-pink-deep rounded-full" style={{ scaleX: scrollYProgress }} />
          </div>
        </motion.div>
      </div>

      <Lightbox index={open} onClose={close} onStep={step} />
      <LanaTribute open={tribute} onClose={closeTribute} />
    </section>
  );
};

export default Gallery;
